// ImagepasteOverlay — HTML overlay rendering the Confirm / Cancel buttons
// for an active imagepaste preview (Phase 7 Task 7-8b).
//
// Positioning:
//   - The drawing engine reports preview state in **canvas (= layer)**
//     coordinates. We translate to **viewport** (client) coordinates via the
//     `CanvasViewMapper` (`viewport.ts` §1b: zoom / pan / flip / rotation in
//     one transform) so the buttons sit on top of the actual rendered
//     preview. (旧: canvas の `getBoundingClientRect()` 比率計算 + flip 再マップ
//     → 回転すると外接矩形に膨らむため mapper へ集約。)
//   - Default anchor: image's **top-right** corner (legacy Java applet
//     convention). When the buttons would overflow the viewport edge we
//     flip to the opposite corner: top-right → top-left when the right
//     edge is too close to the viewport right; → bottom-right when the
//     top is below the viewport top, etc. Every flip is independent so
//     the buttons end up on whichever corner has room.
//   - Final clamp: even after corner-flip we clamp the final (left, top)
//     into the viewport so a *very* small preview rect never pushes the
//     buttons off-screen entirely.
//
// Lock state:
//   - When `preview.locked === true` the user clicked Confirm and the PUT
//     is in flight. Both buttons are disabled and a small spinner sits
//     where Confirm used to be.
//
// Why HTML overlay (not canvas-drawn buttons): keyboard / a11y / focus
// management is essentially free, and the buttons survive resize / DPR
// changes without us tracking pixel-perfect rectangles.

import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { ImagepastePreviewState } from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
import type { CanvasViewMapper } from '../../../infrastructure/drawing/viewport.js';
import {
  CLUSTER_GAP_PX,
  CORNER_KEYS,
  clampHandles,
  HANDLE_HIT_PX,
  HANDLE_VISUAL_PX,
  type HandlePoints,
  handlesEqual,
  intersectsHandleHit,
  type Pt,
} from './imagepasteLayout.js';
import {
  type Corner,
  cornerPoint,
  percentLabel,
  type Rect,
  resizeFromCorner,
  resizeToPercent,
  SCALE_PRESETS_PERCENT,
  type Size,
} from './imagepasteResize.js';
import { buildLayerRows } from './layerPopup.js';

export interface ImagepasteOverlayProps {
  /** 画面 ⇔ canvas 座標の変換窓口 (preview rect を client 座標へ変換する)。 */
  readonly viewMapper: CanvasViewMapper;
  /** Preview state (engine-side coordinates). `null` hides the overlay. */
  readonly preview: ImagepastePreviewState | null;
  /** True while the PUT is in flight. */
  readonly uploading: boolean;
  /** Confirm button click handler. */
  readonly onConfirm: () => void;
  /** Cancel button click handler. */
  readonly onCancel: () => void;
  /**
   * 貼り先レイヤー (= preview スコープの「貼り先専用」layerId) の id.
   * §3.1 B2: `chrome.imagepaste.pasteLayerId`。**アプリのアクティブ描画レイヤー
   * (pen.layerId) ではない**。overlay はこれを「貼り先」として表示し、確定時に
   * useImagepaste がこの値を request.layerId に乗せる。
   * 未指定 (= 貼り先未確定 / 旧呼び出し / レイヤー非対応文脈) のときレイヤー UI を
   * 出さない (= 既存の確定/キャンセルのみ表示、後方互換)。
   */
  readonly activeLayerId?: number;
  /**
   * 貼り先切替ハンドラ (= `chrome.imagepaste.setPasteLayerId`)。§3.1 B2: preview
   * スコープの「貼り先専用」layerId だけを更新し、**アプリのアクティブ描画レイヤー
   * (pen.layerId) は変更しない** (= handleSelectActiveLayer は呼ばない)。
   * `activeLayerId` と対で指定する (片方だけならレイヤー UI を出さない)。
   */
  readonly onSelectLayer?: (layerId: number) => void;
  /**
   * リサイズ UI (角ハンドル + % ボタン) 用。`naturalSize` = 元画像の実寸 (% の基準)、
   * `canvasSize` = キャンバス寸法 (上限)、`onResize` = プレビュー矩形の更新
   * (`engine.setImagepastePreviewRect`)。3 つ揃ったときだけリサイズ UI を出す。
   */
  readonly naturalSize?: Size | null;
  readonly canvasSize?: Size;
  readonly onResize?: (x: number, y: number, width: number, height: number) => void;
}

const CORNERS = CORNER_KEYS;

/** 4 隅の client 座標 (viewport 内へ clamp 済) を rAF で追従 (pan / zoom / 回転は DOM イベントを出さないため)。 */
const useCornerPositions = (
  viewMapper: CanvasViewMapper,
  preview: ImagepastePreviewState | null,
): HandlePoints | null => {
  const [pos, setPos] = useState<HandlePoints | null>(null);
  useEffect(() => {
    if (preview === null || typeof window === 'undefined') {
      setPos(null);
      return;
    }
    const rect: Rect = { x: preview.x, y: preview.y, w: preview.width, h: preview.height };
    const compute = (): HandlePoints =>
      clampHandles(trueCorners(viewMapper, rect), {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    let last = compute();
    setPos((prev) => (prev !== null && handlesEqual(prev, last) ? prev : last));
    let raf = 0;
    const tick = (): void => {
      raf = window.requestAnimationFrame(tick);
      const next = compute();
      if (handlesEqual(next, last)) return;
      last = next;
      setPos(next);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [preview, viewMapper]);
  return pos;
};

const trueCorners = (viewMapper: CanvasViewMapper, rect: Rect): Record<Corner, Pt> => {
  const at = (c: Corner): Pt => {
    const p = cornerPoint(rect, c);
    return viewMapper.canvasToClient(p.x, p.y);
  };
  return { nw: at('nw'), ne: at('ne'), sw: at('sw'), se: at('se') };
};

interface ResizeDrag {
  readonly pointerId: number;
  readonly corner: Corner;
  readonly start: Rect;
  /** pointer と掴んだ角の canvas 座標差 (掴んだ位置で角が跳ねないように補正)。 */
  readonly grabX: number;
  readonly grabY: number;
}

/**
 * Margin (px) between the preview rect edge and the buttons.
 * Small enough that the buttons feel attached, large enough to clear the
 * 1px border the canvas wears.
 */
const BUTTON_MARGIN_PX = CLUSTER_GAP_PX;

/**
 * Approximate footprint of the button cluster (used for clamp / flip before
 * the real cluster is measured on first paint). The cluster now also carries
 * a compact layer switcher (`レイヤー3/2/1` chips), so the *measured* size
 * (via `useLayoutEffect` below) is what actually feeds flip/clamp — these
 * approximations only seed the very first frame.
 */
const APPROX_BUTTONS_WIDTH = 140;
const APPROX_BUTTONS_HEIGHT = 32;

interface ButtonAnchor {
  readonly left: number;
  readonly top: number;
}

/**
 * Compute the final viewport-px (left, top) for the overlay button cluster.
 * Pure function; takes the preview's bounding rect **already in client px**
 * (= `CanvasViewMapper.canvasRectToClientBounds` of the preview rect, which
 * absorbs zoom / pan / flip / rotation) and the viewport size.
 */
export const computeOverlayAnchor = (input: {
  readonly previewBounds: { left: number; top: number; width: number; height: number };
  readonly viewport: { width: number; height: number };
  readonly buttonsSize?: { width: number; height: number };
  /** 角ハンドルの中心 (clamp 済 client px)。あればクラスタがどの hit にも重ならない位置を選ぶ。 */
  readonly handleCenters?: readonly Pt[];
}): ButtonAnchor => {
  const { previewBounds, viewport } = input;
  const buttonsW = input.buttonsSize?.width ?? APPROX_BUTTONS_WIDTH;
  const buttonsH = input.buttonsSize?.height ?? APPROX_BUTTONS_HEIGHT;

  const previewLeftVp = previewBounds.left;
  const previewTopVp = previewBounds.top;
  const previewRightVp = previewLeftVp + previewBounds.width;
  const previewBottomVp = previewTopVp + previewBounds.height;

  const clampLeft = (v: number): number => Math.max(0, Math.min(v, viewport.width - buttonsW));
  const clampTop = (v: number): number => Math.max(0, Math.min(v, viewport.height - buttonsH));
  const aboveTop = previewTopVp - buttonsH - BUTTON_MARGIN_PX;
  const belowTop = previewBottomVp + BUTTON_MARGIN_PX;
  const rightLeft = previewRightVp - buttonsW;

  // Default anchor: image top-right outside the rect (gap = ハンドル hit の半分 + 余白).
  let left = rightLeft;
  let top = aboveTop;
  // Vertical flip: 上が viewport 外なら下辺の外へ。
  if (top < 0) top = belowTop;
  // Horizontal flip: 右が溢れるならプレビュー左端基準へ。
  if (left + buttonsW > viewport.width) left = previewLeftVp;
  // Final clamp: preview 自体が画面外にはみ出すケースでも cluster は viewport 内に残す。
  left = clampLeft(left);
  top = clampTop(top);

  // ハンドル衝突回避: clamp 後に (clamp されたハンドル含む) いずれかの hit と交差するなら、
  // 上/下 x 右/左 の候補から交差しないものを探す。全滅なら従来位置のまま。
  const centers = input.handleCenters;
  if (centers !== undefined && centers.length > 0) {
    const hits = (l: number, t: number): boolean =>
      centers.some((c) =>
        intersectsHandleHit({ left: l, top: t, width: buttonsW, height: buttonsH }, c),
      );
    if (hits(left, top)) {
      const lefts = [rightLeft, previewLeftVp].map(clampLeft);
      const tops = [aboveTop, belowTop].map(clampTop);
      for (const t of tops) {
        for (const l of lefts) {
          if (!hits(l, t)) return { left: l, top: t };
        }
      }
      // 外側に置き場が無い (プレビューが viewport 一杯 / 大きい画像で角が全部 clamp) ときは
      // プレビュー内側の上辺中央へ (角 hit を避ける)。
      const midLeft = clampLeft((previewLeftVp + previewRightVp - buttonsW) / 2);
      for (const t of [
        clampTop(previewTopVp + BUTTON_MARGIN_PX),
        clampTop(previewBottomVp - buttonsH - BUTTON_MARGIN_PX),
      ]) {
        if (!hits(midLeft, t)) return { left: midLeft, top: t };
      }
    }
  }

  return { left, top };
};

/**
 * Hook: re-compute the overlay anchor whenever the preview, the canvas
 * rect (resize / scroll / page zoom), or the window dims change.
 */
const useOverlayAnchor = (
  viewMapper: CanvasViewMapper,
  preview: ImagepastePreviewState | null,
  /**
   * Measured cluster size. When the layer switcher is present the cluster is
   * taller/wider than the 2-button baseline, so passing the *real* size keeps
   * the corner-flip + final clamp honest (= the bottom layer chips never slip
   * off the viewport edge). `null` until first measurement → APPROX_* fallback.
   */
  buttonsSize: { width: number; height: number } | null,
): ButtonAnchor | null => {
  const [anchor, setAnchor] = useState<ButtonAnchor | null>(null);

  useEffect(() => {
    if (preview === null) {
      setAnchor(null);
      return;
    }
    if (typeof window === 'undefined') return;

    const computeNow = (): ButtonAnchor => {
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const handles = clampHandles(
        trueCorners(viewMapper, {
          x: preview.x,
          y: preview.y,
          w: preview.width,
          h: preview.height,
        }),
        viewport,
      );
      return computeOverlayAnchor({
        previewBounds: viewMapper.canvasRectToClientBounds(
          preview.x,
          preview.y,
          preview.width,
          preview.height,
        ),
        viewport,
        handleCenters: CORNERS.map((c) => handles[c]),
        ...(buttonsSize !== null ? { buttonsSize } : {}),
      });
    };
    const recompute = (): void => {
      const next = computeNow();
      // 同値なら state を据え置く (scroll は capture で全要素から飛んでくる / effect の再実行で
      // 毎回 recompute される。新オブジェクトを無条件に set すると再レンダー連鎖の燃料になる)。
      setAnchor((prev) =>
        prev !== null && prev.left === next.left && prev.top === next.top ? prev : next,
      );
    };
    recompute();

    // Resize / scroll handlers — page reflow can move the canvas. Still
    // useful for real window resize / OS-level page zoom.
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true, capture: true });

    // 2026-09-29 viewport transform 移行 (決定5、fable-advisor 指摘 — 感謝、
    // canvas-rotation 段取り 1 以降は mapper が毎回最新の tx/ty/zoom/flip と表示枠の
    // 実位置を読む):
    // 旧 scroll 方式では canvas を包む `<main overflow-auto>` の pan/zoom-anchor
    // 補正が `wrapper.scrollLeft/Top` を書き換え、その scroll イベントが window の
    // capture リスナーまでバブルして recompute を間接的に駆動していた。新方式は
    // `transform: translate3d()` を直接書くだけで DOM イベントが一切発火しない
    // ため、pan ドラッグ中・ズーム中 (ボタン/ホイール/ピンチいずれも) にこの
    // overlay が追従しなくなる (= Confirm/Cancel ボタンが実際のプレビュー位置から
    // ズレたまま固定される回帰)。canvas 自身の `getBoundingClientRect()` は
    // transform 適用後の実測値を毎回返す (React 側の再レンダリング無しに DOM が
    // 動くため) ので、プレビュー表示中だけ軽量な rAF ポーリングで追従させる
    // (= `usePreviewThumbnailLoop` と同じ「変化検知して差分があるときだけ
    // setState」パターン、閉じている間はループ自体を張らないので常時コストは無い)。
    let raf = 0;
    let lastAnchor: ButtonAnchor | null = null;
    const tick = (): void => {
      raf = window.requestAnimationFrame(tick);
      const next = computeNow();
      if (lastAnchor !== null && lastAnchor.left === next.left && lastAnchor.top === next.top) {
        return;
      }
      lastAnchor = next;
      setAnchor((prev) =>
        prev !== null && prev.left === next.left && prev.top === next.top ? prev : next,
      );
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, { capture: true });
      window.cancelAnimationFrame(raf);
    };
    // buttonsSize も依存に含める (= レイヤー UI 出現で cluster 寸法が変わったら
    // アンカーを再計算する)。反転トグルは rAF ポーリングが拾う。
  }, [preview, viewMapper, buttonsSize]);

  return anchor;
};

export const ImagepasteOverlay = (props: ImagepasteOverlayProps): React.ReactNode => {
  const {
    viewMapper,
    preview,
    uploading,
    onConfirm,
    onCancel,
    activeLayerId,
    onSelectLayer,
    naturalSize,
    canvasSize,
    onResize,
  } = props;
  const canResize = !!naturalSize && canvasSize !== undefined && onResize !== undefined;

  // Measure the actual cluster (確定/キャンセル + レイヤー切替) so flip/clamp
  // accounts for the layer UI's extra height/width. APPROX_* until first paint.
  const clusterRef = useRef<HTMLDivElement | null>(null);
  const [clusterSize, setClusterSize] = useState<{ width: number; height: number } | null>(null);

  const anchor = useOverlayAnchor(viewMapper, preview, clusterSize);
  const corners = useCornerPositions(viewMapper, preview);
  const dragRef = useRef<ResizeDrag | null>(null);

  // レイヤー UI を出すのは activeLayerId と onSelectLayer が対で揃ったときだけ
  // (= chrome 配線がある文脈)。片方欠けなら従来の確定/キャンセルのみに倒す。
  const showLayerSwitch = activeLayerId !== undefined && onSelectLayer !== undefined;
  const locked = (preview?.locked ?? false) || uploading;

  // cluster の実寸を測って anchor 計算へ渡す (= レイヤー UI の有無 / lock 中の
  // スピナー差し替えで高さが変わる)。preview / locked / showLayerSwitch が
  // 変わるたび測り直す。paint 前に測って flicker を避けるため useLayoutEffect。
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps は「測り直す契機」のシグナル (= 本体は ref から最新 DOM を読む) であって本体が参照する値ではない。
  useLayoutEffect(() => {
    const el = clusterRef.current;
    if (el === null) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    setClusterSize((prev) =>
      prev !== null && Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1
        ? prev
        : { width: r.width, height: r.height },
    );
    // preview の有無 / lock 中スピナー差し替え / レイヤー UI 出現で cluster の
    // 寸法が変わるので測り直す (= flip/clamp に最新寸法を渡す)。
  }, [preview, locked, showLayerSwitch]);

  if (preview === null || anchor === null) return null;

  const previewRect: Rect = { x: preview.x, y: preview.y, w: preview.width, h: preview.height };

  const onHandleDown = (corner: Corner, e: ReactPointerEvent<HTMLDivElement>): void => {
    if (locked || !canResize || (e.pointerType === 'mouse' && e.button !== 0)) return;
    // キャンバス側のプレビュー移動 / ペン描画へ漏らさない。
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = viewMapper.clientToCanvas(e.clientX, e.clientY);
    const c = cornerPoint(previewRect, corner);
    dragRef.current = {
      pointerId: e.pointerId,
      corner,
      start: previewRect,
      grabX: p.x - c.x,
      grabY: p.y - c.y,
    };
  };
  const onHandleMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (d === null || d.pointerId !== e.pointerId || !naturalSize || !canvasSize || !onResize) {
      return;
    }
    e.stopPropagation();
    const p = viewMapper.clientToCanvas(e.clientX, e.clientY);
    const r = resizeFromCorner({
      start: d.start,
      corner: d.corner,
      pointer: { x: p.x - d.grabX, y: p.y - d.grabY },
      natural: naturalSize,
      canvas: canvasSize,
    });
    onResize(r.x, r.y, r.w, r.h);
  };
  const onHandleUp = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (d === null || d.pointerId !== e.pointerId) return;
    e.stopPropagation();
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const style: CSSProperties = {
    position: 'fixed',
    left: anchor.left,
    top: anchor.top,
    zIndex: 60,
    pointerEvents: 'auto',
  };

  // 上から レイヤー3 / レイヤー2 / レイヤー1 (= LayerPopup と同じ表示順、
  // w3-classic-layers §2.1)。同一 helper を共有して順序/ラベルの二重定義を避ける。
  const layerRows = showLayerSwitch ? buildLayerRows() : [];

  // a11y: 個別ボタンに `aria-label` があるので wrapper には role/aria-label を付けない
  // (Biome useSemanticElements 回避). data 属性は e2e selector 用。
  return (
    <>
      <div
        ref={clusterRef}
        data-oekaki-imagepaste-overlay
        style={style}
        className="flex flex-col gap-1 rounded border border-[var(--color-border)] bg-white/95 px-2 py-1 text-xs shadow-lg"
      >
        {/* 貼り先レイヤー切替 (B 本体)。上から レイヤー3/2/1 のチップ列。
          §3.1 B2: 切替は preview スコープの「貼り先専用」layerId だけを動かし、
          アクティブ描画レイヤー (pen.layerId) は変えない。確定時にこの貼り先が
          request.layerId に乗る。lock 中は disable。 */}
        {showLayerSwitch && (
          // semantic <section aria-label> で「貼り先レイヤー」を意味付け (LayerPopup と
          // 同流儀、role=group の代わりに semantic element で biome 警告を回避)。
          <section
            aria-label="貼り先レイヤー"
            data-oekaki-imagepaste-layer-switch
            className="flex flex-col gap-0.5"
          >
            <span className="px-0.5 text-[10px] text-[var(--color-text-muted)]">貼り先</span>
            <div className="flex items-center gap-1">
              {layerRows.map((row) => {
                const isActive = row.id === activeLayerId;
                return (
                  <button
                    key={row.id}
                    type="button"
                    aria-label={`${row.label}に貼る`}
                    aria-pressed={isActive}
                    disabled={locked}
                    onClick={() => onSelectLayer?.(row.id)}
                    // タッチでも押せる最小サイズ (py を確保)。active は accent 反転、
                    // 非 active は枠線のみ。lock 中は確定/キャンセルと同様 disable。
                    className={`min-w-[44px] border px-1.5 py-1 text-[11px] leading-none disabled:opacity-50 ${
                      isActive
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent)] font-bold text-white'
                        : 'border-[var(--color-border)] bg-white text-black hover:bg-[var(--color-bg-soft)]'
                    }`}
                  >
                    {row.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* リサイズ: 現在倍率 + 25/50/100/200% (元画像実寸基準、中心固定)。 */}
        {canResize && naturalSize && canvasSize && onResize && (
          <section
            aria-label="画像サイズ"
            data-oekaki-imagepaste-resize
            className="flex items-center gap-1"
          >
            <span
              data-oekaki-imagepaste-scale
              className="min-w-[34px] px-0.5 text-right text-[11px] text-black tabular-nums"
            >
              {percentLabel(preview.width, naturalSize)}
            </span>
            {SCALE_PRESETS_PERCENT.map((pct) => (
              <button
                key={pct}
                type="button"
                aria-label={`${pct}%に拡大縮小`}
                disabled={locked}
                onClick={() => {
                  const r = resizeToPercent({
                    rect: previewRect,
                    percent: pct,
                    natural: naturalSize,
                    canvas: canvasSize,
                  });
                  onResize(r.x, r.y, r.w, r.h);
                }}
                className="min-w-[36px] border border-[var(--color-border)] bg-white px-1 py-1 text-[11px] leading-none text-black hover:bg-[var(--color-bg-soft)] disabled:opacity-50"
              >
                {pct}%
              </button>
            ))}
          </section>
        )}

        {/* 確定 / キャンセル (既存)。lock 中はスピナーへ差し替え。 */}
        <div className="flex items-center gap-1">
          {locked ? (
            <span
              role="status"
              aria-live="polite"
              className="flex items-center gap-1 px-1 text-[var(--color-text-muted)]"
            >
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
              />
              アップロード中…
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={onConfirm}
                disabled={locked}
                className="border border-[var(--color-border)] bg-[var(--color-accent)] px-2 py-0.5 font-bold text-white hover:bg-[var(--color-accent-strong,#0050a8)] disabled:opacity-50"
              >
                確定
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={locked}
                className="border border-[var(--color-border)] bg-white px-2 py-0.5 text-black hover:bg-[var(--color-bg-soft)] disabled:opacity-50"
              >
                キャンセル
              </button>
            </>
          )}
        </div>
      </div>
      {canResize && !locked && corners !== null && (
        // 角ハンドル。見た目は小さく、当たり判定は 28px。touch-action:none でタッチ
        // ドラッグがスクロール / パンに化けないようにする。
        <div data-oekaki-imagepaste-handles>
          {CORNERS.map((corner) => {
            const pt = corners[corner];
            return (
              <div
                key={corner}
                data-oekaki-imagepaste-handle={corner}
                data-oekaki-imagepaste-handle-clamped={pt.clamped ? '' : undefined}
                onPointerDown={(e) => onHandleDown(corner, e)}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
                style={{
                  position: 'fixed',
                  left: pt.x - HANDLE_HIT_PX / 2,
                  top: pt.y - HANDLE_HIT_PX / 2,
                  width: HANDLE_HIT_PX,
                  height: HANDLE_HIT_PX,
                  zIndex: 59,
                  touchAction: 'none',
                  cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  aria-hidden="true"
                  // 白背景でも濃い絵の上でも埋もれないよう、accent 塗り + 白縁 + 外側の暗い輪郭。
                  style={{
                    width: HANDLE_VISUAL_PX,
                    height: HANDLE_VISUAL_PX,
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)',
                  }}
                  // viewport 端へ寄せられたハンドルは丸 + 半透明で「角そのものではない」と区別。
                  className={`border-2 border-white bg-[var(--color-accent)] ${
                    pt.clamped ? 'rounded-full opacity-70' : ''
                  }`}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
